import { useEffect, useMemo, useState } from "react";
import { Check, KeyRound, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ErrorMessage, Loading } from "../components/Loading";
import { api } from "../lib/api";

function activationToken() {
  return new URLSearchParams(window.location.hash.replace(/^#/u, "")).get("token") || "";
}

export default function Activate({ onActivated }) {
  const navigate = useNavigate();
  const token = useMemo(activationToken, []);
  const [account, setAccount] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    async function inspect() {
      if (!token) {
        setError("This activation link is incomplete.");
        setLoading(false);
        return;
      }
      try {
        const result = await api("/api/activation/inspect", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
        if (!active) return;
        setAccount(result.account);
        setEmail(result.account.suggestedEmail || "");
      } catch (requestError) {
        if (active) setError(requestError.message);
      } finally {
        if (active) setLoading(false);
      }
    }
    inspect();
    return () => { active = false; };
  }, [token]);

  async function submit(event) {
    event.preventDefault();
    setError("");
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api("/api/activation", {
        method: "POST",
        body: JSON.stringify({ token, email, password }),
      });
      window.history.replaceState(null, "", "/activate");
      await onActivated?.();
      navigate("/members", { replace: true });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="activation-page">
      <div className="activation-page__mark" aria-hidden="true">95</div>
      <div className="activation-card">
        <div className="activation-card__icon"><KeyRound aria-hidden="true" /></div>
        <p className="kicker">Private class account</p>
        <h1>Claim your place in the directory.</h1>
        {loading && <Loading label="Checking your invitation" />}
        {!loading && account && (
          <>
            <p className="activation-card__welcome">
              Welcome, <strong>{account.firstName} {account.lastName}</strong>. Set the email and password you will use to sign in.
            </p>
            {account.role === "administrator" && <p className="role-notice"><ShieldCheck aria-hidden="true" /> This invitation includes administrator access.</p>}
            <form className="activation-form" onSubmit={submit}>
              <label className="form-field">Login email<input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
              <label className="form-field">Password<input type="password" autoComplete="new-password" minLength="12" value={password} onChange={(event) => setPassword(event.target.value)} required /><small>Use at least 12 characters.</small></label>
              <label className="form-field">Confirm password<input type="password" autoComplete="new-password" minLength="12" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <button className="button button--coral" type="submit" disabled={submitting}>{submitting ? "Activating…" : "Activate my account"} <Check aria-hidden="true" /></button>
            </form>
          </>
        )}
        {!loading && !account && error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    </section>
  );
}
